
import React from 'react';
import { Brain, Clock, Music, Heart, Award, Sparkles } from 'lucide-react';

interface BenefitProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const Benefit = ({ icon, title, description }: BenefitProps) => {
  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 transition-transform hover:translate-y-[-5px]">
      <div className="w-12 h-12 flex items-center justify-center bg-piano-gold/10 text-piano-gold rounded-full mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-serif font-semibold mb-3">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

const AdultBeginnerBenefits = () => {
  const benefits = [
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Effektive øvemetoder",
      description: "Lær at maksimere din øvetid med tekniker, der er specielt udviklet til voksne med travle tidsplaner."
    },
    {
      icon: <Music className="w-6 h-6" />,
      title: "Relevant repertoire",
      description: "Fokus på musik, du kender og elsker, så din motivation holdes høj og du hurtigt oplever resultater."
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "Kognitiv sundhed",
      description: "Klaverspil holder hjernen aktiv og skaber nye neurale forbindelser, hvilket er særligt gavnligt for voksne."
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Stress-reduktion",
      description: "Klaverspil fungerer som et kreativt afbræk fra hverdagens stress og kan være en form for mindfulness."
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Personlig præstation",
      description: "Oplev glæden og tilfredsstillelsen ved at mestre en ny færdighed og nå mål, du måske har haft i mange år."
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Voksenvenlig tilgang",
      description: "Undervisningsmetoder, der respekterer din livs- og læringssituation, uden børnepædagogik eller nedladenhed."
    }
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-serif font-bold mb-4">
            Fordele ved klaverundervisning for voksne begyndere
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Det er faktisk en fordel at starte klaverspil som voksen - du har motivation, modenhed og evnen til at forstå sammenhænge, som børn ikke har.
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <Benefit 
              key={index}
              icon={benefit.icon}
              title={benefit.title}
              description={benefit.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default AdultBeginnerBenefits;
